import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MushinLogo from '@/components/MushinLogo';
import BookDemoDialog from '@/components/BookDemoDialog';
import SearchDialog from '@/components/SearchDialog';
import { ChevronDown, Menu, X, Search } from 'lucide-react';

const productLinks = [
  { name: 'Mushin Factory OS', path: '/products/factory-os', desc: 'The operating system for the modern factory' },
  { name: 'Mushin Vendor Connect', path: '/products/vendor-connect', desc: 'Supplier quality and vendor governance platform' },
];

const industryLinks = [
  { name: 'Automotive', path: '/industries/automotive' },
  { name: 'Aerospace', path: '/industries/aerospace' },
  { name: 'Pharmaceuticals', path: '/industries/pharmaceuticals' },
  { name: 'FMCG', path: '/industries/fmcg' },
  { name: 'Export Manufacturing', path: '/industries/export-manufacturing' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);

  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const scrollToIndustries = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    setIndustriesOpen(false);
    const el = document.getElementById('industries');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#060606]/80 border-b border-white/[0.06]" data-testid="main-header">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0" data-testid="header-logo-link">
              <MushinLogo shadow />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {/* Products */}
              <div className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
                <button className="flex items-center gap-1 px-4 py-2 text-[15px] text-white/60 hover:text-white transition-colors font-semibold" data-testid="nav-products">
                  Products <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {productsOpen && (
                  <div className="absolute top-full left-0 pt-2 w-[320px]">
                    <div className="bg-[#111111] border border-white/[0.08] rounded-xl p-2 shadow-2xl shadow-black/40">
                      {productLinks.map(link => (
                        <Link key={link.path} to={link.path}
                          className="block px-4 py-3 rounded-lg hover:bg-white/[0.06] transition-colors"
                          data-testid={`nav-${link.path.split('/').pop()}`}>
                          <div className="text-sm font-bold text-white">{link.name}</div>
                          <div className="text-xs text-white/40 mt-0.5">{link.desc}</div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Industries */}
              <div className="relative" onMouseEnter={() => setIndustriesOpen(true)} onMouseLeave={() => setIndustriesOpen(false)}>
                <button className="flex items-center gap-1 px-4 py-2 text-[15px] text-white/60 hover:text-white transition-colors font-semibold" data-testid="nav-industries">
                  Industries <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {industriesOpen && (
                  <div className="absolute top-full left-0 pt-2 w-[220px]">
                    <div className="bg-[#111111] border border-white/[0.08] rounded-xl p-2 shadow-2xl shadow-black/40">
                      {industryLinks.map((link, i) => (
                        <Link key={i} to={link.path} onClick={() => setIndustriesOpen(false)}
                          className="block px-4 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors cursor-pointer"
                          data-testid={`nav-industry-${i}`}>
                          {link.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Manufacturing 4.0 */}
              <Link to="/manufacturing-4" className="px-4 py-2 text-[15px] text-white/60 hover:text-white transition-colors font-semibold" data-testid="nav-manufacturing4">
                Manufacturing 4.0
              </Link>

              {/* Security & Deployment */}
              <Link to="/security" className="px-4 py-2 text-[15px] text-white/60 hover:text-white transition-colors font-semibold" data-testid="nav-security">
                Security & Deployment
              </Link>

              {/* About */}
              <Link to="/about" className="px-4 py-2 text-[15px] text-white/60 hover:text-white transition-colors font-semibold" data-testid="nav-about">
                About
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setSearchOpen(true)}
                className="hidden lg:flex items-center gap-2 text-white/30 hover:text-white/60 transition-colors px-3 py-1.5 rounded-lg border border-white/[0.06] hover:border-white/[0.12] bg-white/[0.02]"
                data-testid="search-trigger"
              >
                <Search className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="text-[12px] font-medium">Search</span>
                <kbd className="hidden xl:flex items-center text-[10px] text-white/15 font-mono ml-3 border border-white/[0.06] rounded px-1 py-0.5 leading-none">
                  <span className="text-[9px]">&#8984;</span>K
                </kbd>
              </button>
              <button
                onClick={() => setSearchOpen(true)}
                className="lg:hidden text-white/40 hover:text-white/70 transition-colors p-1.5"
                data-testid="search-trigger-mobile"
              >
                <Search className="w-[18px] h-[18px]" strokeWidth={2} />
              </button>
              <a href="mailto:sales@mushinlabs.com" className="hidden sm:inline-flex text-sm text-white/40 hover:text-[#E02D45] font-semibold transition-colors px-3 py-2" data-testid="nav-get-quote">
                Get a Quote
              </a>
              <button onClick={() => setDemoOpen(true)}
                className="bg-[#E02D45] hover:bg-[#C82539] text-white px-6 py-2 rounded-full text-sm font-bold transition-colors"
                data-testid="book-demo-cta">
                Book a Demo
              </button>
              <button className="lg:hidden text-white/60 p-1" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden bg-[#0A0A0A] border-t border-white/[0.06] px-4 pb-6 pt-2 max-h-[80vh] overflow-auto">
            <nav className="flex flex-col">
              <p className="text-xs font-bold uppercase tracking-wider text-white/20 px-3 pt-4 pb-2">Products</p>
              {productLinks.map(l => (
                <Link key={l.path} to={l.path} className="px-3 py-2.5 text-sm font-medium text-white/60 hover:text-white" onClick={() => setMobileOpen(false)}>{l.name}</Link>
              ))}
              <p className="text-xs font-bold uppercase tracking-wider text-white/20 px-3 pt-4 pb-2">Industries</p>
              {industryLinks.map((l, i) => (
                <Link key={i} to={l.path} className="px-3 py-2 text-sm font-medium text-white/60 hover:text-white" onClick={() => setMobileOpen(false)}>{l.name}</Link>
              ))}
              <div className="border-t border-white/[0.06] my-3" />
              <Link to="/manufacturing-4" className="px-3 py-2.5 text-sm font-bold text-white/60 hover:text-white" onClick={() => setMobileOpen(false)} data-testid="nav-mobile-manufacturing4">Manufacturing 4.0</Link>
              <Link to="/security" className="px-3 py-2.5 text-sm font-bold text-white/60 hover:text-white" onClick={() => setMobileOpen(false)} data-testid="nav-mobile-security">Security & Deployment</Link>
              <Link to="/about" className="px-3 py-2.5 text-sm font-bold text-white/60 hover:text-white" onClick={() => setMobileOpen(false)} data-testid="nav-mobile-about">About</Link>
              <a href="mailto:sales@mushinlabs.com" className="px-3 py-2.5 text-sm font-bold text-[#E02D45]">Get a Quote</a>
            </nav>
          </div>
        )}
      </header>

      <BookDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
